import { useState } from "react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { AlertTriangle, ArrowUpRight, ArrowDownRight, ChartNoAxesCombined } from "lucide-react";
import { money, shortDate, statusClass } from "../lib/api";

const PIE_COLORS = ["#0A1628", "#2FB6E4", "#00D4FF", "#c77912", "#4f46e5", "#f97316"];

const CARD_NAV = ["invoices", "expenses", "reports", "invoices", "purchases", "inventory"];

export default function Dashboard({ data, currency, onPayment, onOpenInvoice, onNavigate }) {
  const [period, setPeriod] = useState("year");
  if (!data) {
    return <div className="skeleton-grid"><div /><div /><div /><div /></div>;
  }

  const k = data.kpis || {};
  const d = data.deltas || {};
  const fullTrend = data.trend || [];
  const trend = period === "quarter" ? fullTrend.slice(-3) : fullTrend.slice(-6);
  const barData = fullTrend.slice(-6);

  const fmtDelta = (v) => `${v > 0 ? "+" : ""}${v ?? 0}%`;
  const cards = [
    { label: "Revenue collected", value: k.revenue, tone: "green", delta: fmtDelta(d.revenue), num: d.revenue },
    { label: "Total expenses", value: k.expenses, tone: "amber", delta: fmtDelta(d.expenses), num: d.expenses },
    { label: "Net profit", value: k.profit, tone: "dark", delta: fmtDelta(d.profit), num: d.profit },
    { label: "Receivables", value: k.receivables, tone: "rose", delta: `${(data.invoices || []).filter((i) => i.status === "Overdue").length} overdue`, num: null },
    { label: "Payables", value: k.payables, tone: "indigo", delta: "PO to pay", num: null },
    { label: "Inventory value", value: k.inventory_value, tone: "sky", delta: "Live", num: null },
  ];

  const expenseBreakdown = (data.expenses || []).reduce((acc, expense) => {
    const key = expense.category || "Other";
    const existing = acc.find((x) => x.name === key);
    if (existing) existing.value += expense.amount || 0;
    else acc.push({ name: key, value: expense.amount || 0 });
    return acc;
  }, []);

  const outstanding = (data.invoices || []).filter((i) => i.status !== "Paid").slice(0, 5);
  const lowStock = (data.products || []).filter((p) => p.stock <= p.reorder_level).slice(0, 5);
  const tooltipFmt = (value) => money(value, currency);

  return (
    <>
      <div className="kpi-grid">
        {cards.map((card, index) => (
          <button className={`kpi-card ${card.tone}`} key={card.label} data-testid={`kpi-${index}`} onClick={() => onNavigate?.(CARD_NAV[index])} title={`View ${card.label}`}>
            <div className="kpi-top">
              <span>{card.label}</span>
              <span className="kpi-icon"><ChartNoAxesCombined size={16} /></span>
            </div>
            <strong>{money(card.value, currency)}</strong>
            <div className="kpi-change">
              {card.num === null ? (
                <span className="up">↗ {card.delta}</span>
              ) : (
                <span className={card.num >= 0 ? "up" : "down"}>{card.num >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />} {card.delta}</span>
              )}
              <small>vs last month</small>
            </div>
          </button>
        ))}
      </div>
      <div className="dashboard-grid">
        <section className="panel chart-panel">
          <div className="panel-heading">
            <div><h3>Revenue vs expenses</h3><p>Cash movement across {period === "quarter" ? "the last quarter" : "the trailing six months"}</p></div>
            <select data-testid="period-select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="year">This year</option>
              <option value="quarter">This quarter</option>
            </select>
          </div>
          <div className="legend"><span><i className="dot emerald"></i>Revenue</span><span><i className="dot gold"></i>Expenses</span></div>
          <div className="chart">
            {trend.length === 0 ? (
              <div className="mini-empty">No revenue or expenses yet — they’ll chart here as you invoice and spend.</div>
            ) : (
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 720, height: 225 }}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2FB6E4" stopOpacity={0.34} />
                    <stop offset="95%" stopColor="#2FB6E4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} width={44} />
                <Tooltip formatter={tooltipFmt} cursor={{ stroke: "#2FB6E4", strokeWidth: 1, strokeDasharray: "4 4" }} contentStyle={{ borderRadius: 10, border: "1px solid #e3eaf2", fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#2FB6E4" fill="url(#rev)" strokeWidth={3} activeDot={{ r: 5 }} />
                <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#0A1628" fill="none" strokeWidth={2} strokeDasharray="5 5" activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </section>
        <section className="panel sales-panel">
          <div className="panel-heading">
            <div><h3>Sales rhythm</h3><p>Monthly revenue trend</p></div>
            <button data-testid="record-payment-button" className="link-button" onClick={onPayment}>Record payment <ArrowUpRight size={14} /></button>
          </div>
          <div className="sales-total">{money(k.revenue, currency)} <span className={d.revenue >= 0 ? "" : "down"}>{fmtDelta(d.revenue)}</span></div>
          <div className="bar-chart">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 420, height: 183 }}>
              <BarChart data={barData}>
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <Tooltip formatter={tooltipFmt} cursor={{ fill: "rgba(47,182,228,.1)" }} contentStyle={{ borderRadius: 10, border: "1px solid #e3eaf2", fontSize: 12 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#0A1628" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>
      <div className="lower-grid">
        <section className="panel">
          <div className="panel-heading">
            <div><h3>Outstanding invoices</h3><p>Keep your cash moving</p></div>
          </div>
          <div className="invoice-list">
            {outstanding.length === 0 && <div className="mini-empty">Every invoice is settled. Beautiful.</div>}
            {outstanding.map((invoice) => (
              <button className="invoice-row" key={invoice.id} data-testid={`invoice-row-${invoice.number}`} onClick={() => onOpenInvoice(invoice)}>
                <div className="invoice-avatar">{invoice.customer_name?.[0]}</div>
                <div className="invoice-main">
                  <strong>{invoice.customer_name}</strong>
                  <small>{invoice.number} · Due {shortDate(invoice.due_date)}</small>
                </div>
                <div className="invoice-amount">
                  <strong>{money(invoice.total, currency)}</strong>
                  <span className={`status ${statusClass(invoice.status)}`}>{invoice.status}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
        <section className="panel">
          <div className="panel-heading">
            <div><h3>Expense breakdown</h3><p>Where your cash is going</p></div>
            {expenseBreakdown.length > 0 && <button className="link-button" data-testid="view-expenses-link" onClick={() => onNavigate?.("expenses")}>View all <ArrowUpRight size={14} /></button>}
          </div>
          {expenseBreakdown.length === 0 ? (
            <div className="mini-empty">No expenses recorded yet.</div>
          ) : (
            <div className="pie-wrap">
              <ResponsiveContainer width="100%" height={190} initialDimension={{ width: 170, height: 190 }}>
                <PieChart>
                  <Pie data={expenseBreakdown} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={2}>
                    {expenseBreakdown.map((_, index) => <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value) => money(value, currency)} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="pie-legend">
                {expenseBreakdown.map((item, index) => (
                  <li key={item.name}>
                    <i style={{ background: PIE_COLORS[index % PIE_COLORS.length] }}></i>
                    <span>{item.name}</span>
                    <strong>{money(item.value, currency)}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>
      <div className="lower-grid">
        <section className="panel">
          <div className="panel-heading">
            <div><h3>Stock watch</h3><p>Items that need your attention</p></div>
          </div>
          <div className="stock-list">
            {lowStock.length === 0 && <div className="mini-empty">All products are comfortably stocked.</div>}
            {lowStock.map((product) => (
              <button className="stock-row stock-row-btn" key={product.id} data-testid={`stock-row-${product.id}`} onClick={() => onNavigate?.("inventory")}>
                <div>
                  <strong>{product.name}</strong>
                  <small>{product.sku} · {product.stock} {product.unit}s left</small>
                </div>
                <span className="stock-alert"><AlertTriangle size={14} /> Reorder</span>
              </button>
            ))}
          </div>
        </section>
        <section className="panel">
          <div className="panel-heading">
            <div><h3>Recent activity</h3><p>Every meaningful move in your workspace</p></div>
          </div>
          <div className="activity-list">
            {(data.payments || []).slice(0, 3).map((payment) => (
              <div className="activity-row" key={payment.id}>
                <span className="activity-dot green"></span>
                <div>
                  <strong>Payment received</strong>
                  <small>{payment.customer_name} paid {money(payment.amount, currency)} on {payment.invoice_number}</small>
                </div>
                <em>{shortDate(payment.created_at)}</em>
              </div>
            ))}
            {(data.expenses || []).slice(0, 3).map((expense) => (
              <div className="activity-row" key={expense.id}>
                <span className="activity-dot amber"></span>
                <div>
                  <strong>Expense recorded</strong>
                  <small>{expense.description || expense.category} · {money(expense.amount, currency)}</small>
                </div>
                <em>{shortDate(expense.date)}</em>
              </div>
            ))}
            {(data.payments || []).length + (data.expenses || []).length === 0 && (
              <div className="mini-empty">Activity will appear here as your business moves.</div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
